import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CookieConsentProvider } from "@/context/CookieConsentContext";
import { Toaster } from "@/components/ui/toaster";
import CookieConsent from "@/components/CookieConsent";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import PageTransition from "@/components/motion/PageTransition";
import Spinner from "@/components/ui/Spinner";
import Home from "@/pages/Home";
import QuienesSomos from "@/pages/QuienesSomos";
import EquipoMiembro from "@/pages/EquipoMiembro";
import Formacion from "@/pages/Formacion";
import Eventos from "@/pages/Eventos";
import Recursos from "@/pages/Recursos";
import MisioneroDigital from "@/pages/MisioneroDigital";
import Contacto from "@/pages/Contacto";
import Noticias from "@/pages/Noticias";
import NoticiaDetalle from "@/pages/NoticiaDetalle";
import PalabraDelDia from "@/pages/PalabraDelDia";
import EvangeliosAnteriores from "@/pages/EvangeliosAnteriores";
import PoliticaPrivacidad from "@/pages/PoliticaPrivacidad";
import PoliticaCookies from "@/pages/PoliticaCookies.js";
import Podcast from "@/pages/Podcast";
import DescargaApp from "@/pages/DescargaApp";
import NoEncontrada from "@/pages/NoEncontrada";
import "@/App.css";

// ── Panel de administración: carga diferida ──────────────────────────────────
// Antes estos veinte módulos —incluido el editor TipTap completo— viajaban en
// el bundle inicial que descarga cualquier visitante anónimo que solo quiere
// leer el Evangelio del día. Ahora solo se piden cuando se entra en /admin.
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminHomeEditor = lazy(() => import("@/pages/admin/AdminHomeEditor"));
const AdminPageEditor = lazy(() => import("@/pages/admin/AdminPageEditor"));
const AdminEquipo = lazy(() => import("@/pages/admin/AdminEquipo"));
const AppAdminLogin = lazy(() => import("@/pages/admin/app/AppAdminLogin"));
const AdminPanel = lazy(() => import("@/pages/admin/app/AdminPanel"));
const AdminRoute = lazy(() => import("@/components/admin/AdminRoute"));

const Layout = ({ children }) => (
  <>
    <Navbar />
    <main>
      <PageTransition>{children}</PageTransition>
    </main>
    <Footer />
  </>
);

// Reserva de pantalla completa mientras llega el chunk de administración.
const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Spinner label="Cargando el panel" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <CookieConsentProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/quienes-somos" element={<Layout><QuienesSomos /></Layout>} />
              <Route path="/quienes-somos/equipo/:slug" element={<Layout><EquipoMiembro /></Layout>} />
              <Route path="/noticias" element={<Layout><Noticias /></Layout>} />
              <Route path="/noticias/:slug" element={<Layout><NoticiaDetalle /></Layout>} />
              <Route path="/palabra-del-dia" element={<Layout><PalabraDelDia /></Layout>} />
              <Route path="/palabra-del-dia/archivo" element={<Layout><EvangeliosAnteriores /></Layout>} />
              <Route path="/formacion" element={<Layout><Formacion /></Layout>} />
              <Route path="/eventos" element={<Layout><Eventos /></Layout>} />
              <Route path="/recursos" element={<Layout><Recursos /></Layout>} />
              <Route path="/misionero-digital" element={<Layout><MisioneroDigital /></Layout>} />
              <Route path="/contacto" element={<Layout><Contacto /></Layout>} />
              <Route path="/app" element={<Layout><DescargaApp /></Layout>} />
              <Route path="/podcast" element={<Layout><Podcast /></Layout>} />
              <Route path="/politica-de-privacidad" element={<Layout><PoliticaPrivacidad /></Layout>} />
              <Route path="/politica-de-cookies" element={<Layout><PoliticaCookies /></Layout>} />

              <Route path="/admin/login" element={<Suspense fallback={<AdminFallback />}><AdminLogin /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<AdminFallback />}><AdminDashboard /></Suspense>} />
              <Route path="/admin/portada" element={<Suspense fallback={<AdminFallback />}><AdminHomeEditor /></Suspense>} />
              <Route path="/admin/paginas/:slug" element={<Suspense fallback={<AdminFallback />}><AdminPageEditor /></Suspense>} />
              <Route path="/admin/equipo" element={<Suspense fallback={<AdminFallback />}><AdminEquipo /></Suspense>} />

              {/* Panel de administración de la app */}
              <Route path="/admin/app/login" element={<Suspense fallback={<AdminFallback />}><AppAdminLogin /></Suspense>} />
              <Route
                path="/admin/app/*"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <AdminRoute><AdminPanel /></AdminRoute>
                  </Suspense>
                }
              />

              {/* Comodín. Sin esta ruta, una URL inexistente no coincidía con nada
                  y <Routes> devolvía null: página en blanco, sin navbar ni pie. */}
              <Route path="*" element={<Layout><NoEncontrada /></Layout>} />
            </Routes>
            <Toaster />
            {/* Fuera de <Routes>: el aviso de cookies debe verse en cualquier
                página, no solo en la de inicio. */}
            <CookieConsent />
          </BrowserRouter>
        </AuthProvider>
      </CookieConsentProvider>
    </ErrorBoundary>
  );
}

export default App;
